/**
 * printInvoices.js v1.2
 * Fetches full order data and opens a print-ready window for selected order refs.
 */

import { supabase } from '../supabaseClient';

const VERSION = 'v1.2';

const COMPANY = {
  name:    'Hobby City 2018 Ltd',
  gst:     '125-132-574',
  address: '62 Lunn Avenue, Mount Wellington, Auckland 1072, New Zealand',
  phone:   '09 570 1495',
  email:   'sales@hobbycity.nz',
  website: 'www.hobbycity.nz',
  bank:    'Westpac · Hobby City 2018 Ltd · 03-1392-0039100-003',
};

const fmtMoney = (n) =>
  n == null ? '$0.00' : `$${Number(n).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  const date = dt.toLocaleDateString('en-NZ', { day: '2-digit', month: 'long', year: 'numeric' });
  const time = dt.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  return `${date} ${time}`;
};

async function fetchOrderData(orderRefs) {
  // Fetch SO headers
  const { data: headers } = await supabase
    .from('sales_order_header')
    .select('*')
    .in('order_ref', orderRefs);

  if (!headers || !headers.length) return [];

  const orderIds = headers.map(h => h.order_id);

  // Fetch line items
  const { data: items } = await supabase
    .from('sales_order_items')
    .select('*')
    .in('order_id', orderIds);

  // Fetch customer + PO info from the view (already has customer name & address joined)
  const { data: viewRows } = await supabase
    .from('v_received_po_open_orders')
    .select('order_ref, customer_name, address1, address2, city, state, post_code, country, email, phone, mobile, po_ref, fully_received_date, stage, delivery_name, delivery_address1, delivery_address2, delivery_city, delivery_state, delivery_post_code, delivery_country, delivery_instructions, order_total, amount_paid, balance_due')
    .in('order_ref', orderRefs);

  // Build maps keyed by order_ref — collect ALL rows to capture per-item po_ref
  const viewMap = {};         // first row for header data
  const viewAllRows = {};     // all rows: { order_ref: [rows] }
  for (const row of (viewRows || [])) {
    if (!viewMap[row.order_ref]) viewMap[row.order_ref] = row;
    if (!viewAllRows[row.order_ref]) viewAllRows[row.order_ref] = [];
    viewAllRows[row.order_ref].push(row);
  }

  // Build unique po details per order from view rows
  const orderPoDetails = {};  // { order_ref: [ { po_ref, fully_received_date, stage } ] }
  for (const row of (viewRows || [])) {
    if (row.po_ref) {
      if (!orderPoDetails[row.order_ref]) orderPoDetails[row.order_ref] = [];
      const existing = orderPoDetails[row.order_ref];
      if (!existing.find(p => p.po_ref === row.po_ref)) {
        existing.push({
          po_ref:              row.po_ref,
          fully_received_date: row.fully_received_date,
          stage:               row.stage,
        });
      }
    }
  }

  // Fetch PO items to build itemPoRefMap: { order_ref: { item_code: po_ref } }
  const poRefs = [...new Set((viewRows || []).map(r => r.po_ref).filter(Boolean))];
  let poItemCodes = {}; // { po_ref: Set of item_codes }
  const itemPoRefMap = {}; // { order_ref: { item_code: po_ref } }

  if (poRefs.length) {
    const { data: poHeaders } = await supabase
      .from('purchase_order_header')
      .select('order_id, order_ref')
      .in('order_ref', poRefs);
    const poOrderIds = (poHeaders || []).map(h => h.order_id);
    const poRefByOrderId = {};
    for (const h of (poHeaders || [])) poRefByOrderId[h.order_id] = h.order_ref;

    if (poOrderIds.length) {
      const { data: poItems } = await supabase
        .from('purchase_order_items')
        .select('order_id, item_code')
        .in('order_id', poOrderIds);

      // Build poItemCodes (legacy)
      for (const pi of (poItems || [])) {
        const ref = poRefByOrderId[pi.order_id];
        if (!poItemCodes[ref]) poItemCodes[ref] = new Set();
        poItemCodes[ref].add(pi.item_code);
      }

      // Build itemPoRefMap: for each SO order, map its item_codes to the PO they came from
      for (const soRef of Object.keys(orderPoDetails)) {
        itemPoRefMap[soRef] = {};
        for (const poDetail of orderPoDetails[soRef]) {
          const poRef = poDetail.po_ref;
          const itemsForPo = (poItems || []).filter(pi => poRefByOrderId[pi.order_id] === poRef);
          for (const pi of itemsForPo) {
            if (!itemPoRefMap[soRef][pi.item_code]) {
              itemPoRefMap[soRef][pi.item_code] = poRef;
            }
          }
        }
      }
    }
  }

  // Fetch current stock for all item codes
  const allCodes = [...new Set((items || []).map(i => i.item_code).filter(Boolean))];
  let stockMap = {};
  if (allCodes.length) {
    const { data: stock } = await supabase
      .from('product_stock')
      .select('code, stock_on_hand, available')
      .in('code', allCodes);
    for (const s of (stock || [])) stockMap[s.code] = s;
  }

  return headers.map(hdr => ({
    header:       hdr,
    items:        (items || []).filter(i => i.order_id === hdr.order_id),
    view:         viewMap[hdr.order_ref] || null,
    poItemCodes:  poItemCodes[viewMap[hdr.order_ref]?.po_ref] || new Set(),
    itemPoRefMap: itemPoRefMap[hdr.order_ref] || {},
    poDetails:    orderPoDetails[hdr.order_ref] || [],
    stockMap,
  }));
}

function buildInvoiceHTML(orders) {
  const calcDispatchStatus = (items, stockMap) => {
    const anyMoved    = items.some(i => Number(i.item_qty_moved || 0) > 0);
    const outstanding = items.filter(i => Number(i.item_qty) > Number(i.item_qty_moved || 0));
    if (!outstanding.length) return 'FULL';
    const allCovered  = outstanding.every(i => {
      const soh = Number(stockMap[i.item_code]?.stock_on_hand ?? 0);
      return soh >= (Number(i.item_qty) - Number(i.item_qty_moved || 0));
    });
    if (!allCovered) return 'PARTIAL_WAITING';
    return anyMoved ? 'PARTIAL_DISPATCHED' : 'FULL';
  };

  const invoicePages = orders.map(({ header: h, items, view: v, poItemCodes, itemPoRefMap, poDetails, stockMap }) => {
    const customerName = v?.customer_name
      || h.company
      || [h.first_name, h.last_name].filter(Boolean).join(' ')
      || '—';

    const billingLines = [v?.address1, v?.address2, v?.city, v?.state, v?.post_code, v?.country];
    const billingAddr  = billingLines.filter(Boolean).join('<br>');

    const email = v?.email || h.email || '';
    const phone = [v?.phone || h.phone, v?.mobile || h.mobile].filter(Boolean).join(' / ');

    const deliveryName  = v?.delivery_name || customerName;
    const deliveryLines = [
      v?.delivery_address1, v?.delivery_address2,
      v?.delivery_city, v?.delivery_state,
      v?.delivery_post_code, v?.delivery_country,
    ];
    const deliveryAddr         = deliveryLines.filter(Boolean).join('<br>') || billingAddr;
    const deliveryInstructions = v?.delivery_instructions || '';

    const subTotal    = h.sub_total ?? items.reduce((s, i) => s + (Number(i.item_total) || 0), 0);
    const balanceDue  = v?.balance_due ?? (h.total ?? 0);
    const hasBalance  = Number(balanceDue) > 0.005;
    const taxTotal    = h.tax_total ?? 0;
    const total       = h.total ?? (subTotal + taxTotal);
    const invoiceDate = fmtDate(h.invoice_date || h.created_date);

    // Build one line per unique PO involved in this order
    const poLines = poDetails.length
      ? poDetails.map(p =>
          `${p.po_ref} &nbsp;·&nbsp; Received: ${fmtDateTime(p.fully_received_date)} &nbsp;·&nbsp; Stage: ${p.stage || h.stage || '—'}`
        )
      : v
        ? [`${v.po_ref} &nbsp;·&nbsp; Received: ${fmtDateTime(v.fully_received_date)} &nbsp;·&nbsp; Stage: ${v.stage || h.stage || '—'}`]
        : (h.stage ? [`Stage: ${h.stage}`] : []);

    // Dispatch status — matches the logic on the ReceivedPOs page
    const dispatchStatus = calcDispatchStatus(items, stockMap);

    const lineRows = items.map(item => `
      <tr>
        <td class="code">${item.item_code || '—'}</td>
        <td class="num moved-col">${Number(item.item_qty_moved || 0).toLocaleString()}</td>
        <td class="po-flag">${itemPoRefMap[item.item_code]
          ? `<span class="po-y">Y</span> <span class="po-ref-inline">${itemPoRefMap[item.item_code]}</span>`
          : ''
        }</td>
        <td class="desc">${item.item_name || '—'}${item.item_option_1 ? ` <span class="opt">${item.item_option_1}</span>` : ''}</td>
        <td class="num">${Number(item.item_qty || 0).toLocaleString()}</td>
        <td class="num">${fmtMoney(item.item_price)}</td>
        <td class="num">${item.item_discount ? `${item.item_discount}%` : '—'}</td>
        <td class="num">${fmtMoney(item.item_total)}</td>
        <td class="tick"><span class="tickbox"></span></td>
        <td class="tick"><span class="tickbox"></span></td>
      </tr>`).join('');

    return `
    <div class="invoice">
      <!-- Dispatch status banner -->
      <div class="inv-dispatch-banner inv-dispatch-banner--${dispatchStatus.toLowerCase()}">
        ${{
          'FULL':               'FULL · DISPATCH',
          'PARTIAL_DISPATCHED': 'PARTIAL · DISPATCH',
          'PARTIAL_WAITING':    'PARTIAL · WAITING',
        }[dispatchStatus] || dispatchStatus}
      </div>

      <!-- Header bar -->
      <div class="inv-header">
        <span class="inv-title">${hasBalance ? 'Tax Invoice &nbsp;<span style="color:#dc2626;font-weight:700">— Do Not Ship · Balance Due</span>' : 'Tax Invoice'}</span>
        <span class="inv-meta-inline">
          <span class="inv-meta-item"><span class="inv-meta-label">Date</span>${invoiceDate}</span>
          <span class="inv-meta-sep">·</span>
          <span class="inv-meta-item"><span class="inv-meta-label">Invoice No</span><strong>${h.invoice_no || h.order_ref}</strong></span>
          <span class="inv-meta-sep">·</span>
          <span class="inv-meta-item"><span class="inv-meta-label">Order Ref</span>${h.order_ref}</span>
          ${h.customer_po_no ? `<span class="inv-meta-sep">·</span><span class="inv-meta-item"><span class="inv-meta-label">Customer PO</span>${h.customer_po_no}</span>` : ''}
          <span class="inv-meta-sep">·</span>
          <span class="inv-meta-item"><span class="inv-meta-label">Status</span>${h.stage || '—'}</span>
          ${poLines.length ? `<span class="inv-meta-sep">·</span><span class="inv-meta-item inv-po-inline">${poLines.join('<br>')}</span>` : ''}
        </span>
      </div>

      <!-- Addresses -->
      <div class="inv-addresses">
        <div class="inv-addr">
          <div class="inv-addr__label">Customer</div>
          <div class="inv-addr__name">${customerName}</div>
          ${billingAddr ? `<div class="inv-addr__body">${billingAddr}</div>` : ''}
          <div class="inv-addr__body">
            ${email ? `${email}<br>` : ''}
            ${phone ? `Phone: ${phone}` : ''}
          </div>
        </div>
        <div class="inv-addr">
          <div class="inv-addr__label">Ship To</div>
          <div class="inv-addr__name">${deliveryName}</div>
          ${deliveryAddr ? `<div class="inv-addr__body">${deliveryAddr}</div>` : ''}
          ${deliveryInstructions ? `<div class="inv-addr__body inv-addr__note">${deliveryInstructions}</div>` : ''}
        </div>
      </div>

      <!-- Line items -->
      <table class="inv-lines">
        <thead>
          <tr>
            <th class="code">Code</th>
            <th class="num moved-col">Moved</th>
            <th class="po-flag">PO</th>
            <th class="desc">Item</th>
            <th class="num">Qty</th>
            <th class="num">Unit Price</th>
            <th class="num">Discount</th>
            <th class="num">Subtotal</th>
            <th class="tick">P</th>
            <th class="tick">D</th>
          </tr>
        </thead>
        <tbody>${lineRows}</tbody>
      </table>

      <!-- Totals + notes -->
      <div class="inv-footer">
        <div class="inv-footer__left">
          <div class="inv-note"><strong>Payment Terms</strong><br>Full payment prior to dispatch</div>
          <div class="inv-note" style="margin-top:8px">
            <strong>Bank Details</strong><br>${COMPANY.bank}
          </div>
          <div class="inv-note" style="margin-top:8px;font-style:italic;color:#666">
            All goods come with a manufacturer's warranty. Any factory faults will be repaired.
            Please read the manual as it contains important warranty information.
          </div>
        </div>
        <div class="inv-footer__right">
          <table class="inv-totals">
            ${h.freight_description || h.freight_total ? `<tr><td>${h.freight_description || 'Freight'}</td><td>${fmtMoney(h.freight_total)}</td></tr>` : ''}
            <tr><td>Order Total</td><td>${fmtMoney(v?.order_total ?? total)}</td></tr>
            <tr><td>Amount Paid</td><td>${fmtMoney(v?.amount_paid ?? 0)}</td></tr>
            <tr class="total-row${hasBalance ? ' balance-due--red' : ''}"><td>Balance Due (NZD)</td><td>${fmtMoney(v?.balance_due ?? total)}</td></tr>
          </table>
        </div>
      </div>

      <div class="inv-co-footer">${COMPANY.name} &nbsp;·&nbsp; GST: ${COMPANY.gst} &nbsp;·&nbsp; ${COMPANY.address} &nbsp;·&nbsp; P ${COMPANY.phone} &nbsp;·&nbsp; E ${COMPANY.email} &nbsp;·&nbsp; W ${COMPANY.website} &nbsp;·&nbsp; ${VERSION}</div>
      <div class="inv-thankyou">Thanks for shopping with us. Have a great day.</div>
    </div>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Tax Invoices — Hobby City</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap');

    * { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      font-family: 'Lato', Arial, sans-serif;
      font-size: 11px;
      color: #1a1a1a;
      background: #fff;
    }

    .invoice {
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 16mm 16mm 12mm;
      page-break-after: always;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .invoice:last-child { page-break-after: avoid; }

    /* Header */
    .inv-header {
      display: flex;
      align-items: baseline;
      gap: 12px;
      padding-bottom: 10px;
      border-bottom: 2px solid #1a1a1a;
      flex-wrap: wrap;
    }
    .inv-title {
      font-size: 16px;
      font-weight: 700;
      letter-spacing: 0.04em;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .inv-meta-inline {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 4px;
      font-size: 10px;
      color: #333;
    }
    .inv-meta-label {
      color: #888;
      margin-right: 3px;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }
    .inv-meta-item { white-space: nowrap; }
    .inv-meta-sep { color: #bbb; }
    .inv-po-inline { color: #555; font-size: 10px; }
    .inv-co-footer {
      text-align: center;
      font-size: 8px;
      color: #999;
      padding-top: 6px;
      border-top: 1px solid #eee;
      line-height: 1.6;
    }

    /* Addresses */
    .inv-addresses {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
      padding: 8px 0;
      border-bottom: 1px solid #ddd;
    }
    .inv-addr__label {
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #888;
      margin-bottom: 4px;
    }
    .inv-addr__name { font-weight: 700; font-size: 12px; margin-bottom: 4px; }
    .inv-addr__body { color: #444; line-height: 1.45; }

    /* Line items */
    .inv-lines {
      width: 100%;
      border-collapse: collapse;
      flex: 1;
    }
    .inv-lines thead tr {
      background: #1a1a1a;
      color: #fff;
    }
    .inv-lines th {
      padding: 5px 8px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
    .inv-lines td {
      padding: 4px 8px;
      border-bottom: 1px solid #eee;
      vertical-align: top;
      line-height: 1.35;
    }
    .inv-lines tr:last-child td { border-bottom: none; }
    .inv-lines .code { width: 96px; font-family: monospace; font-size: 10px; }
    .inv-lines .moved-col { width: 48px; text-align: right; }
    .inv-lines .po-flag { width: 28px; text-align: center; }
    .po-y { font-weight: 700; color: #1a1a1a; font-size: 11px; }
    .po-ref-inline { font-size: 10px; color: #555; font-family: monospace; white-space: nowrap; }
    .inv-lines .po-flag { width: 78px; text-align: left; }
    .inv-lines .num { text-align: right; white-space: nowrap; }
    .inv-lines .tick { width: 26px; text-align: center; padding-left: 2px; padding-right: 2px; }
    .inv-lines th.tick { letter-spacing: 0; }
    .tickbox {
      display: inline-block;
      width: 13px;
      height: 13px;
      border: 1px solid #1a1a1a;
      border-radius: 2px;
      vertical-align: middle;
    }

    /* Dispatch banner */
    .inv-dispatch-banner {
      text-align: center;
      font-size: 24px;
      font-weight: 900;
      letter-spacing: 0.18em;
      padding: 2px 0 0;
    }
    .inv-dispatch-banner--full               { color: #16a34a; }
    .inv-dispatch-banner--partial_dispatched  { color: #2563eb; }
    .inv-dispatch-banner--partial_waiting     { color: #9ca3af; }
    .opt { color: #888; font-size: 10px; }

    /* Footer */
    .inv-footer {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 24px;
      padding-top: 10px;
      border-top: 1px solid #ddd;
    }
    .inv-footer__left { flex: 1; }
    .inv-footer__right { flex-shrink: 0; }
    .inv-note { font-size: 10px; color: #444; line-height: 1.6; }

    .inv-totals { border-collapse: collapse; min-width: 220px; }
    .inv-totals td { padding: 4px 10px; }
    .inv-totals td:first-child { color: #666; }
    .inv-totals td:last-child { text-align: right; font-weight: 600; }
    .inv-totals tr.balance-due--red td { color: #dc2626; }
    .inv-totals tr.total-row td {
      font-size: 13px;
      font-weight: 700;
      border-top: 2px solid #1a1a1a;
      padding-top: 6px;
    }

    .inv-thankyou {
      text-align: center;
      font-style: italic;
      color: #888;
      font-size: 10px;
      padding-top: 6px;
      border-top: 1px solid #eee;
    }

    /* Print */
    @page { size: A4; margin: 10mm; }

    @media print {
      body { background: #fff; }
      /* The @page box supplies the margin. Keeping the fixed 210x297mm block
         here adds page margin on top of a full-sheet element, which overflows
         a few mm onto a near-empty second page. */
      .invoice {
        width: auto;
        min-height: 0;
        margin: 0;
        padding: 0;
        gap: 8px;
        break-after: page;
        page-break-after: always;
      }
      .invoice:last-child { break-after: auto; page-break-after: avoid; }
      .inv-lines { flex: none; }
      .inv-lines tr { break-inside: avoid; page-break-inside: avoid; }
      .no-print { display: none !important; }
    }

    /* Screen preview */
    @media screen {
      body { background: #f0f0f0; padding: 20px 0; }
      .invoice {
        background: #fff;
        box-shadow: 0 2px 16px rgba(0,0,0,0.12);
        margin-bottom: 24px;
      }
      .print-bar {
        position: fixed;
        top: 0; left: 0; right: 0;
        background: #1a1a1a;
        color: #fff;
        padding: 10px 24px;
        display: flex;
        align-items: center;
        gap: 16px;
        z-index: 100;
        font-family: 'Lato', Arial, sans-serif;
        font-size: 13px;
      }
      .print-bar button {
        background: #fff;
        color: #1a1a1a;
        border: none;
        border-radius: 4px;
        padding: 6px 18px;
        font-weight: 700;
        font-size: 13px;
        cursor: pointer;
      }
      .print-bar button:hover { background: #e8e8e8; }
      body { padding-top: 52px; }
    }
  </style>
</head>
<body>
  <div class="print-bar no-print">
    <span>🖨 ${orders.length} invoice${orders.length !== 1 ? 's' : ''} ready</span>
    <button onclick="window.print()">Print / Save PDF</button>
    <button onclick="window.close()" style="background:#555;color:#fff">Close</button>
  </div>
  ${invoicePages}
</body>
</html>`;
}

export async function printInvoices(orderRefs) {
  if (!orderRefs.length) return;

  const win = window.open('', '_blank');
  win.document.write('<html><body><p style="font-family:sans-serif;padding:40px">Loading invoices…</p></body></html>');

  try {
    const orders = await fetchOrderData(orderRefs);
    if (!orders.length) {
      win.document.write('<html><body><p style="font-family:sans-serif;padding:40px">No order data found. Orders may not be in the replicated tables yet.</p></body></html>');
      return;
    }
    const html = buildInvoiceHTML(orders);
    win.document.open();
    win.document.write(html);
    win.document.close();
  } catch (err) {
    win.document.write(`<html><body><p style="font-family:sans-serif;padding:40px;color:red">Error: ${err.message}</p></body></html>`);
  }
}
